/**
 * VerbindungenTab - Klient-spezifisches Netzwerk + Verknüpfte Betreuungen
 * 
 * Oben: Verknüpfte Betreuungen (Ehepartner, Verwandte etc.)
 * Unten: Institutionen und Personen aus dem Netzwerk
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Building2,
  UserCircle,
  Users,
  RefreshCw,
  Plus,
  Heart,
  Scale,
  Banknote,
  Home,
  Briefcase,
  Stethoscope,
  Phone,
  Mail,
  MapPin,
  Link2,
  Share2,
  Network,
  ArrowUpRight,
  Search,
  X,
  Trash2,
  UserPlus,
  LinkIcon,
  Loader2,
  FolderOpen,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { useTabs } from '@/contexts/TabContext';
import { betreuungAPI } from '@/api/betreuungAPI';
import BetreuerVerlauf from '@/components/BetreuerVerlauf';
import KlientMiniGraph from '@/components/KlientMiniGraph';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

const BEZIEHUNG_ICONS = {
  versichert_bei: Heart,
  leistung_von: Banknote,
  schulden_bei: Scale,
  wohnt_in: Home,
  arbeitet_bei: Briefcase,
  betreut_von: Users,
  bank_bei: Banknote,
  vermieter: Home,
  angehoerig: Users,
  behandelt_von: Stethoscope,
  rechtspfleger_fuer: Scale,
  sachbearbeiter_fuer: Briefcase,
  pflegekraft_fuer: Heart,
  ansprechpartner_fuer: Phone,
  sonstiges: Link2,
};

const BEZIEHUNG_FARBEN = {
  versichert_bei: 'bg-pink-100 text-pink-700',
  leistung_von: 'bg-green-100 text-green-700',
  schulden_bei: 'bg-red-100 text-red-700',
  wohnt_in: 'bg-blue-100 text-blue-700',
  arbeitet_bei: 'bg-orange-100 text-orange-700',
  betreut_von: 'bg-purple-100 text-purple-700',
  bank_bei: 'bg-yellow-100 text-yellow-700',
  vermieter: 'bg-indigo-100 text-indigo-700',
  angehoerig: 'bg-rose-100 text-rose-700',
  behandelt_von: 'bg-teal-100 text-teal-700',
  rechtspfleger_fuer: 'bg-slate-100 text-slate-700',
  sachbearbeiter_fuer: 'bg-cyan-100 text-cyan-700',
  pflegekraft_fuer: 'bg-emerald-100 text-emerald-700',
  ansprechpartner_fuer: 'bg-sky-100 text-sky-700',
  sonstiges: 'bg-gray-100 text-gray-700',
};

const BEZIEHUNG_OPTIONEN = [
  { value: 'ehepartner', label: 'Ehepartner/in' },
  { value: 'kind', label: 'Kind' },
  { value: 'elternteil', label: 'Elternteil' },
  { value: 'geschwister', label: 'Geschwister' },
  { value: 'verwandt', label: 'Verwandt' },
  { value: 'mitbewohner', label: 'Mitbewohner/in' },
  { value: 'sonstiges', label: 'Sonstiges' },
];

const GEGEN_BEZIEHUNG = {
  ehepartner: 'ehepartner',
  kind: 'elternteil',
  elternteil: 'kind',
  geschwister: 'geschwister',
  verwandt: 'verwandt',
  mitbewohner: 'mitbewohner',
  sonstiges: 'sonstiges',
};

// ─────────────────────────────────────────────────────────────
// Verknüpfte Betreuungen Subkomponente
// ─────────────────────────────────────────────────────────────

function VerknuepfteBetreuungen({ klientId, klientName }) {
  const { openTab } = useTabs();
  const [verknuepfungen, setVerknuepfungen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedKlient, setSelectedKlient] = useState(null);
  const [beziehung, setBeziehung] = useState('ehepartner');
  const [saving, setSaving] = useState(false);

  const loadVerknuepfungen = useCallback(async () => {
    try {
      setLoading(true);
      const data = await betreuungAPI.getKlientVerknuepfungen(klientId);
      setVerknuepfungen(data);
    } catch {
      // silent — no linked clients is normal
      setVerknuepfungen([]);
    } finally {
      setLoading(false);
    }
  }, [klientId]);

  useEffect(() => {
    if (klientId) loadVerknuepfungen();
  }, [klientId, loadVerknuepfungen]);

  const handleSearch = async (q) => {
    setSearchQuery(q);
    if (q.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const all = await betreuungAPI.listKlienten({ search: q });
      // Eigene ID + bereits verknüpfte ausfiltern
      const linkedIds = new Set(verknuepfungen.map(v => v.klient_id));
      linkedIds.add(klientId);
      setSearchResults(all.filter(k => !linkedIds.has(k.id)));
    } catch {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleLink = async () => {
    if (!selectedKlient) return;
    setSaving(true);
    try {
      await betreuungAPI.addKlientVerknuepfung(klientId, {
        ziel_klient_id: selectedKlient.id,
        beziehung: beziehung,
        gegen_beziehung: GEGEN_BEZIEHUNG[beziehung] || beziehung,
        notiz: '',
      });
      toast.success(`${selectedKlient.vorname} ${selectedKlient.nachname} verknüpft`);
      setShowForm(false);
      setSelectedKlient(null);
      setSearchQuery('');
      setSearchResults([]);
      await loadVerknuepfungen();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUnlink = async (zielId, name) => {
    if (!window.confirm(`Verknüpfung mit ${name} wirklich entfernen?`)) return;
    try {
      await betreuungAPI.removeKlientVerknuepfung(klientId, zielId);
      toast.success('Verknüpfung entfernt');
      await loadVerknuepfungen();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const openKlientAkte = (kid, name) => {
    openTab(`/akten/${kid}`, name);
  };

  return (
    <Card data-testid="verknuepfte-betreuungen-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <LinkIcon className="w-5 h-5 text-amber-600" />
            Verknüpfte Betreuungen
          </CardTitle>
          <Button
            variant={showForm ? 'secondary' : 'outline'}
            size="sm"
            onClick={() => { setShowForm(!showForm); setSelectedKlient(null); setSearchQuery(''); setSearchResults([]); }}
            data-testid="toggle-link-form-btn"
          >
            {showForm ? <X className="w-4 h-4 mr-1" /> : <UserPlus className="w-4 h-4 mr-1" />}
            {showForm ? 'Abbrechen' : 'Betreuung verknüpfen'}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Verknüpfungs-Formular */}
        {showForm && (
          <div className="mb-5 p-4 rounded-lg border-2 border-dashed border-amber-200 bg-amber-50/50 space-y-3" data-testid="link-form">
            <p className="text-sm font-medium text-amber-800">Klient suchen und verknüpfen</p>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Name eingeben..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-9"
                data-testid="link-search-input"
              />
              {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-slate-400" />}
            </div>

            {/* Suchergebnisse */}
            {searchResults.length > 0 && !selectedKlient && (
              <div className="max-h-48 overflow-y-auto border rounded-lg bg-white divide-y" data-testid="link-search-results">
                {searchResults.map((k) => (
                  <button
                    key={k.id}
                    className="w-full text-left px-3 py-2.5 hover:bg-amber-50 transition-colors flex items-center justify-between"
                    onClick={() => { setSelectedKlient(k); setSearchResults([]); }}
                    data-testid={`link-result-${k.id}`}
                  >
                    <div>
                      <span className="font-medium text-slate-800">{k.vorname} {k.nachname}</span>
                      {k.aktenzeichen && <span className="text-xs text-slate-400 ml-2">Az: {k.aktenzeichen}</span>}
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {k.status || 'aktiv'}
                    </Badge>
                  </button>
                ))}
              </div>
            )}

            {/* Ausgewählt → Beziehung wählen */}
            {selectedKlient && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 p-2 rounded-lg bg-white border">
                  <UserCircle className="w-5 h-5 text-amber-600 shrink-0" />
                  <span className="font-medium">{selectedKlient.vorname} {selectedKlient.nachname}</span>
                  <Badge variant="outline" className="text-xs ml-auto">{selectedKlient.status || 'aktiv'}</Badge>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setSelectedKlient(null)}>
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
                <div className="flex items-center gap-3">
                  <Select value={beziehung} onValueChange={setBeziehung}>
                    <SelectTrigger className="w-48" data-testid="beziehung-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {BEZIEHUNG_OPTIONEN.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button onClick={handleLink} disabled={saving} data-testid="confirm-link-btn">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <LinkIcon className="w-4 h-4 mr-1" />}
                    Verknüpfen
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Liste verknüpfter Klienten */}
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-slate-400 mr-2" />
            <span className="text-slate-500 text-sm">Laden...</span>
          </div>
        ) : verknuepfungen.length === 0 ? (
          <div className="text-center py-8" data-testid="no-links-message">
            <Users className="w-10 h-10 mx-auto text-slate-200 mb-2" />
            <p className="text-slate-400 text-sm">Keine verknüpften Betreuungen</p>
            <p className="text-slate-400 text-xs mt-1">z.B. Ehepartner, Kinder oder Verwandte die ebenfalls betreut werden</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="linked-clients-list">
            {verknuepfungen.map((v) => {
              const fullName = `${v.vorname || ''} ${v.nachname || ''}`.trim();
              return (
                <div
                  key={v.klient_id}
                  className="flex items-center gap-3 p-3 rounded-lg border bg-white hover:bg-slate-50 transition-all group"
                  data-testid={`linked-client-${v.klient_id}`}
                >
                  <div
                    className="flex-1 min-w-0 cursor-pointer"
                    onClick={() => openKlientAkte(v.klient_id, fullName)}
                  >
                    <div className="flex items-center gap-2">
                      <UserCircle className="w-5 h-5 text-amber-600 shrink-0" />
                      <span className="font-medium text-slate-800 truncate">{fullName || 'Unbekannt'}</span>
                      <ArrowUpRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-amber-500 transition-colors shrink-0" />
                    </div>
                    <div className="flex items-center gap-2 mt-1 ml-7">
                      <Badge className="bg-amber-100 text-amber-700 text-xs">{v.beziehung || 'verwandt'}</Badge>
                      <Badge variant="outline" className="text-xs">{v.status || 'aktiv'}</Badge>
                      {v.aktenzeichen && <span className="text-xs text-slate-400">Az: {v.aktenzeichen}</span>}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 text-slate-300 hover:text-red-500 hover:bg-red-50 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => handleUnlink(v.klient_id, fullName)}
                    data-testid={`unlink-btn-${v.klient_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


// ─────────────────────────────────────────────────────────────
// Hauptkomponente
// ─────────────────────────────────────────────────────────────

export default function VerbindungenTab({ klientId, klientName, betreuung }) {
  const { openTab } = useTabs();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [netzwerkExpanded, setNetzwerkExpanded] = useState(true);
  // Bubble-Grafik optional: Standardsicht sind Listen/Karten (Betreuerwechsel, Institutionen, Personen)
  const [grafikOffen, setGrafikOffen] = useState(false);

  const loadNetzwerk = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/netzwerk/klient/${klientId}/netzwerk`);
      if (!res.ok) throw new Error('Fehler beim Laden');
      setData(await res.json());
    } catch {
      // Netzwerk-API evtl. nicht erreichbar → kein Fehler-Toast
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (klientId) loadNetzwerk();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [klientId]);

  const openInstitutionDetail = (inst) => {
    openTab(`/netzwerk?typ=institution&id=${inst.id}`, inst.name);
  };

  const openPersonDetail = (person) => {
    openTab(`/netzwerk?typ=person&id=${person.id}`, person.vorname ? `${person.vorname} ${person.nachname}` : person.name);
  };

  const openGlobalGraph = () => {
    openTab(`/netzwerk`, 'Netzwerk');
  };

  const openNewLink = () => {
    openTab(`/netzwerk?addLink=true&klientId=${klientId}`, 'Neue Verknüpfung');
  };

  const institutionen = data?.institutionen || [];
  const personen = data?.personen || [];
  const statistik = data?.statistik || {};

  return (
    <div className="space-y-6" data-testid="verbindungen-tab">
      {/* ═══════ BETREUERWECHSEL (klare Liste mit seit/bis + Quelle) ═══════ */}
      <BetreuerVerlauf betreuung={betreuung} />

      {/* ═══════ VERKNÜPFTE BETREUUNGEN ═══════ */}
      <VerknuepfteBetreuungen klientId={klientId} klientName={klientName} />

      {/* ═══════ NETZWERK (Institutionen & Personen) ═══════ */}
      <div>
        <button
          className="w-full flex items-center justify-between py-2 text-left"
          onClick={() => setNetzwerkExpanded(!netzwerkExpanded)}
          data-testid="toggle-netzwerk-section"
        >
          <h3 className="text-lg font-semibold flex items-center gap-2 text-slate-700">
            <Network className="w-5 h-5 text-blue-600" />
            Netzwerk
            <span className="text-sm font-normal text-slate-400">
              (Institutionen & Personen)
            </span>
          </h3>
          {netzwerkExpanded ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
        </button>

        {netzwerkExpanded && (
          <div className="space-y-4 mt-2">
            {/* Header-Actions */}
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={loadNetzwerk}>
                <RefreshCw className="w-4 h-4 mr-1" /> Aktualisieren
              </Button>
              <Button variant="outline" size="sm" onClick={openGlobalGraph} data-testid="open-global-graph">
                <Share2 className="w-4 h-4 mr-1" /> Netzwerk-Graph
              </Button>
              <Button size="sm" onClick={openNewLink} data-testid="add-connection-btn">
                <Plus className="w-4 h-4 mr-1" /> Verknüpfung
              </Button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 animate-spin text-slate-400 mr-3" />
                <span className="text-slate-500">Netzwerk wird geladen...</span>
              </div>
            ) : !data ? (
              <div className="text-center py-8">
                <Network className="w-10 h-10 mx-auto text-slate-200 mb-2" />
                <p className="text-slate-400 text-sm">Netzwerk nicht verfügbar</p>
              </div>
            ) : (
              <>
                {/* Statistik */}
                <div className="grid grid-cols-3 gap-4">
                  <Card className="bg-teal-50/50">
                    <CardContent className="pt-4 pb-3 text-center">
                      <Building2 className="w-6 h-6 mx-auto text-teal-600 mb-1" />
                      <p className="text-2xl font-bold text-teal-700">{statistik?.institutionen_anzahl || 0}</p>
                      <p className="text-xs text-teal-600">Institutionen</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-purple-50/50">
                    <CardContent className="pt-4 pb-3 text-center">
                      <UserCircle className="w-6 h-6 mx-auto text-purple-600 mb-1" />
                      <p className="text-2xl font-bold text-purple-700">{statistik?.personen_anzahl || 0}</p>
                      <p className="text-xs text-purple-600">Personen</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-blue-50/50">
                    <CardContent className="pt-4 pb-3 text-center">
                      <Briefcase className="w-6 h-6 mx-auto text-blue-600 mb-1" />
                      <p className="text-2xl font-bold text-blue-700">{statistik?.massnahmen_anzahl || 0}</p>
                      <p className="text-xs text-blue-600">Aktive Leistungen</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Mini-Graph: optional, standardmaessig zugeklappt */}
                <Card>
                  <CardHeader className="pb-2">
                    <button className="w-full flex items-center justify-between text-left" onClick={() => setGrafikOffen(o => !o)} data-testid="toggle-beziehungsnetz-grafik">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Network className="w-5 h-5 text-blue-600" />
                        Beziehungsnetz (Grafik)
                      </CardTitle>
                      <span className="text-xs text-blue-700 flex items-center gap-1">
                        {grafikOffen ? 'ausblenden' : 'anzeigen'}
                        {grafikOffen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </span>
                    </button>
                  </CardHeader>
                  {grafikOffen && (
                    <CardContent>
                      <KlientMiniGraph klientId={klientId} onOpenGlobal={openGlobalGraph} />
                    </CardContent>
                  )}
                </Card>

                {/* Institutionen */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Building2 className="w-5 h-5 text-teal-600" />
                      Institutionen ({institutionen.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {institutionen.length > 0 ? (
                      <div className="space-y-3">
                        {institutionen.map((item, idx) => {
                          const inst = item.institution;
                          return (
                            <div
                              key={inst.id || idx}
                              className="p-4 rounded-lg border hover:shadow-md transition-all cursor-pointer group bg-white hover:bg-teal-50/30"
                              onClick={() => openInstitutionDetail(inst)}
                              data-testid={`verbindung-institution-${idx}`}
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <p className="font-medium text-slate-800">{inst.name}</p>
                                    <ArrowUpRight className="w-4 h-4 text-slate-300 group-hover:text-teal-500 transition-colors shrink-0" />
                                  </div>
                                  <p className="text-xs text-slate-500 mt-0.5">{inst.typ || inst.rechtsform || 'Institution'}</p>
                                  {inst.adresse && (
                                    <p className="text-xs text-slate-400 flex items-center gap-1 mt-1">
                                      <MapPin className="w-3 h-3" /> {inst.adresse} {inst.plz} {inst.ort}
                                    </p>
                                  )}
                                  {(inst.telefon || inst.email) && (
                                    <div className="flex gap-3 mt-1 text-xs text-slate-400">
                                      {inst.telefon && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{inst.telefon}</span>}
                                      {inst.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{inst.email}</span>}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="flex flex-wrap gap-1.5 mt-3">
                                {item.beziehungen?.map((bez, bIdx) => {
                                  const Icon = BEZIEHUNG_ICONS[bez.art] || Link2;
                                  return (
                                    <Badge
                                      key={bIdx}
                                      className={`${BEZIEHUNG_FARBEN[bez.art] || 'bg-gray-100 text-gray-700'} text-xs flex items-center gap-1`}
                                    >
                                      <Icon className="w-3 h-3" />
                                      {bez.label}
                                      {bez.details && <span className="opacity-70">({bez.details})</span>}
                                      {bez.betrag && <span className="font-bold ml-1">{bez.betrag.toLocaleString('de-DE')} EUR</span>}
                                    </Badge>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-6">
                        <Building2 className="w-8 h-8 mx-auto text-slate-200 mb-2" />
                        <p className="text-slate-400 text-sm">Keine Institutionen verknüpft</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Personen */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <UserCircle className="w-5 h-5 text-purple-600" />
                      Personen ({personen.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {personen.length > 0 ? (
                      <div className="space-y-3">
                        {personen.map((item, idx) => {
                          const p = item.person;
                          const name = p.vorname ? `${p.vorname} ${p.nachname}` : p.name || 'Unbekannt';
                          return (
                            <div
                              key={p.id || idx}
                              className="p-4 rounded-lg border hover:shadow-md transition-all cursor-pointer group bg-white hover:bg-purple-50/30"
                              onClick={() => openPersonDetail(p)}
                              data-testid={`verbindung-person-${idx}`}
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <p className="font-medium text-slate-800">{name}</p>
                                    {p.rolle && <Badge variant="secondary" className="text-xs">{p.rolle}</Badge>}
                                    <ArrowUpRight className="w-4 h-4 text-slate-300 group-hover:text-purple-500 transition-colors shrink-0" />
                                  </div>
                                  {(p.telefon || p.email) && (
                                    <div className="flex gap-3 mt-1 text-xs text-slate-400">
                                      {p.telefon && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{p.telefon}</span>}
                                      {p.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{p.email}</span>}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="flex flex-wrap gap-1.5 mt-3">
                                {item.beziehungen?.map((bez, bIdx) => {
                                  const Icon = BEZIEHUNG_ICONS[bez.art] || Link2;
                                  return (
                                    <Badge
                                      key={bIdx}
                                      className={`${BEZIEHUNG_FARBEN[bez.art] || 'bg-gray-100 text-gray-700'} text-xs flex items-center gap-1`}
                                    >
                                      <Icon className="w-3 h-3" />
                                      {bez.label}
                                      {bez.details && <span className="opacity-70">({bez.details})</span>}
                                    </Badge>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-6">
                        <UserCircle className="w-8 h-8 mx-auto text-slate-200 mb-2" />
                        <p className="text-slate-400 text-sm">Keine Personen verknüpft</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
